import React from "react"

import { useToast } from "@supliful/common"
import { formatFileName } from "@supliful/domain"

interface Args {
  path: string
  getSignedS3Url: (key: string, fileType: string) => Promise<string>
}

export type UploadFile = {
  fileKey: string
  fileName: string
  fileType: string | null
  fileUrl: string
}

export function useS3Upload({
  path,
  getSignedS3Url,
}: Args): [(file: File) => Promise<UploadFile | void>, { loading: boolean }] {
  const [loading, setLoading] = React.useState(false)
  const toast = useToast()

  async function upload(file: File) {
    setLoading(true)
    let parsedKey = path
    if (parsedKey[parsedKey.length - 1] === "/") {
      parsedKey = parsedKey.slice(0, -1)
    }
    if (parsedKey[0] === "/") {
      parsedKey = parsedKey.substr(1)
    }
    const formattedName = formatFileName(file.name)
    const key = parsedKey + "/" + formattedName

    const s3URL = await getSignedS3Url(key, file.type).catch((err) => {
      setLoading(false)
      console.error(err)
      toast({
        status: "error",
        description: "Error uploading file, please try again",
      })
      return null
    })

    if (!s3URL) {
      setLoading(false)
      return
    }

    const fileUrl = await fetch(s3URL, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    })
      .then((data) => {
        return data.url
      })
      .catch(() => {
        toast({ status: "error", description: "Error uploading file" })
      })
    setLoading(false)
    return {
      fileKey: key,
      fileName: file.name,
      fileType: file.type || null,
      fileUrl: fileUrl ? fileUrl?.split("?")[0] : "",
    }
  }

  return [upload, { loading }]
}
